<template>
  <el-col :span="24" id="bank-recon" v-loading.body="(receiving || changingPage) && !navigating" element-loading-text="Fetching page...">
    <el-row type="flex" justify="start" align="middle" class="padded-s" id="bank-recon-header">
      <el-col :span="10" class="h-padded left-aligned-col">
        <div style="display: block;">
          <h2 style="margin: 0; font-weight: 100; display: inline-block;">BANK RECONCILIATION</h2>
          <p class="stats">{{ meta.count ? `${meta.count} results` : 'No results' }} </p>
          <p v-if="meta.count" class="stats">{{ meta.shown ? `${meta.shown} shown` : '' }}</p>
        </div>
      </el-col>
      <el-col :span="4">
        <el-row type="flex" justify="center" v-if="meta.count > 20">
          <el-pagination
            layout="prev, pager, next, jumper"
            :total="meta.count"
            :page-size="20"
            @current-change="changePage"
            :current-page="currentPage"
          />
        </el-row>
      </el-col>
      <el-col :span="10">
        <el-row type="flex" justify="end">
          <el-button
            v-if="availableBanks.length <= 1"
            plain
            @click="() => handleDropdownMenuClick(null)"
            :disabled="!availableBanks.length">
            New Bank Reconciliation
          </el-button>
          <el-dropdown v-else-if="availableBanks && availableBanks.length > 1" @command="handleDropdownMenuClick">
            <el-button type="primary">
              New Bank Reconciliation<i class="el-icon-caret-bottom el-icon--right" />
            </el-button>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item v-for="bank in availableBanks" :key="bank.id" :command="bank.id">
                {{ bank.name }}
              </el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </el-row>
      </el-col>
    </el-row>
    <el-row type="flex" justify="center" align="middle">
      <el-col :span="bankRecs.length ? 24 : 12">
        <el-table
          v-if="bankRecs.length"
          id="bankrec-table"
          ref="bankrecsTable"
          :data="bankRecs"
          border
          style="width: 100%; z-index: 0;"
          :maxHeight="tableHeight">
          <el-table-column prop="bank" label="Bank">
            <template slot-scope="scope">
              <el-row type="flex" align="middle">
                <router-link :to="{name: 'ViewBankRec', params: { id: scope.row.id }}">
                  {{ getBankById(scope.row.bank).name }}
                </router-link>
              </el-row>
            </template>
          </el-table-column>
          <el-table-column prop="start" label="Statement Start Date" width="160" />
          <el-table-column prop="end" label="Statement End Date" width="160" />
          <el-table-column prop="balance_decimal" label="Statement Balance" width="175" align="right">
            <template slot-scope="scope">
              {{ formatAmount(scope.row) || '' }}
            </template>
          </el-table-column>
          <el-table-column prop="completed_time" label="Status" width="120" align="center">
            <template slot-scope="scope">
              <el-row type="flex" align="middle" justify="center">
                <el-tag :type="scope.row.completed_time ? 'success' : 'danger'" close-transition>
                  {{ scope.row.completed_time ? 'Complete' : 'Draft' }}
                </el-tag>
              </el-row>
            </template>
          </el-table-column>
        </el-table>
      </el-col>
    </el-row>
  </el-col>
</template>

<script>
  import { mapGetters } from 'vuex'
  import store from '@/store/store'
  import { formatLocale } from '@/utils/helpers/StringsNumbers'

  export default {
    data () {
      return {
        tableHeight: 1000,
        dialogFormVisible: false,
        changingPage: false
      }
    },
    methods: {
      handleDropdownMenuClick (bankId = null) {
        if (bankId) {
          this.$router.push({ name: 'NewBankRec', query: { bank: bankId } })
        } else {
          this.$router.push({ name: 'NewBankRec', query: { bank: this.availableBanks[0].id } })
        }
      },
      /**
       * Handles paginating between Bankrec pages
       * @param page  Target page
       */
      async changePage (page) {
        try {
          await this.$store.dispatch('clearBankRecState')
          await this.$store.dispatch('fetchBankRecsByPage', page)
        } catch (e) {
          console.log(e)
        } finally {
          this.changingPage = false
        }
      },
      /**
       * Formats dollar amounts with thousands separators
       * @param bankRec
       * @return {*}
       */
      /* eslint-disable camelcase */
      formatAmount (bankRec) {
        const { bank, balance_decimal } = bankRec
        const bankModel = this.getBankById(bank)
        let currencyModel = null
        if (bankModel && bankModel.currency) {
          currencyModel = this.getCurrencyById(bankModel.currency)
        }
        let bal = balance_decimal ? formatLocale(balance_decimal) : ''
        if (bal && currencyModel && currencyModel.symbol) {
          bal = `${currencyModel.symbol}${bal}`
        }
        return bal
      }
    },
    computed: {
      ...mapGetters({
        banks: 'getAllBanks',
        defaultBank: 'getDefaultBank',
        meta: 'getBankRecMetadata',
        bankRecs: 'getBankRecs',
        getBankById: 'getBankById',
        getCurrencyById: 'getCurrencyById',
        currentPage: 'getCurrentBankRecPage',
        receiving: 'getBankrecFetchStatus',
        navigating: 'getNavigationStatus'
      }),
      /**
       * Returns a sorted list of bankrecs
       */
      bankRecs () {
        return this.$store.getters.getBankRecs.sort((a, b) => new Date(b.end) - new Date(a.end))
      },
      /**
       * Returns a list of banks that do not have any draft bankrecs
       */
      availableBanks () {
        const drafts = {}
        this.bankRecs.forEach(bankRec => {
          if (bankRec.completed_time === null) {
            drafts[bankRec.bank] = true
          }
        })
        return this.banks.filter(({ id }) => !drafts[id])
      }
    },
    async beforeRouteEnter (to, from, next) {
      try {
        await store.dispatch('clearBankRecState', true)
        const apiResponse = await store.dispatch('fetchBankRecsWithQuery')
        if (apiResponse && apiResponse.entities) {
          let promises = await store.dispatch('findDependencies', apiResponse.entities)
          await Promise.all(promises)
          next()
        }
      } catch (e) {
        console.log(e)
      }
    },
    mounted () {
      this.$nextTick = () => {
        const tableHeight = () => {
          const parentHeight = document.getElementById('bank-recon').offsetHeight
          const headerHeight = document.getElementById('bank-recon-header').offsetHeight
          return parentHeight - headerHeight
        }
        this.tableHeight = tableHeight()
      }
    }
  }
</script>

<style scoped>
  .padded-s {
    padding: 12px;
  }
  #bank-recon-header {
    border-bottom: 1px solid lightgray;
  }
  .h-padded {
    padding: 0 12px;
  }
  .left-aligned-col {
    text-align: left;
  }
  .stats {
    margin: 0;
    display: inline-block;
    padding: 0 3px;
  }
  a, a:visited {
    text-decoration: none;
    color: rgb(0, 0, 238) !important;
  }
</style>



// WEBPACK FOOTER //
// src/components/Programs/BankRecon/BankRecs.vue